// A sound past 100 % in the preview (spec volume-boost). A media element plays at most at its own
// volume of 1, so one asked for more is routed once through the page's one AudioContext, a gain
// carrying the rest. One never asked for more keeps its own way to the speakers, untouched. Our
// elements are CORS-enabled (`mediaCrossOrigin`), so their sound reaches the graph.

/** The gain node of each element routed so far: routing an element cannot be undone. */
const routed = new WeakMap<HTMLMediaElement, GainNode>();
let context: AudioContext | null = null;

/** What an element's sound is boosted by now: 1 when it was never routed. */
export function boostOf(element: HTMLMediaElement): number {
  return routed.get(element)?.gain.value ?? 1;
}

/**
 * Boosts an element's sound by `gain` (1 or more), routing it the first time it is asked for more
 * than 1. A context made before a press starts suspended: it is woken at each boost, which every
 * play follows a press for (a page's activation lasts).
 */
export function setBoost(element: HTMLMediaElement, gain: number): void {
  let node = routed.get(element);
  if (node === undefined) {
    // Without Web Audio (jsdom, an old browser) it plays at its own volume of 1.
    if (gain <= 1 || (context === null && typeof AudioContext === "undefined")) return;
    context ??= new AudioContext();
    node = context.createGain();
    context.createMediaElementSource(element).connect(node).connect(context.destination);
    routed.set(element, node);
  }
  if (context?.state === "suspended") void context.resume();
  node.gain.value = gain;
}
